import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  Grid,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Rating,
  Slider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  Palette,
  Speed,
  Accessibility,
  PhoneAndroid,
  BugReport,
  Lightbulb,
  Star,
  CheckCircle,
} from '@mui/icons-material';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const UIEnhancementTicket = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: 'Enhance User Interface and Experience - Case History',
    description: '',
    priority: 'Medium',
    category1: 'IT Support',
    category2: 'Software Enhancement',
    category3: 'User Interface',
    enhancementType: '',
    currentIssues: [],
    proposedSolutions: [],
    userImpact: 3,
    implementationComplexity: 3,
    urgency: 'Medium',
    customDescription: '',
  });

  const [captchaToken, setCaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Predefined enhancement types
  const enhancementTypes = [
    { value: 'visual_design', label: 'Visual Design Improvements', icon: <Palette /> },
    { value: 'performance', label: 'Performance Optimization', icon: <Speed /> },
    { value: 'accessibility', label: 'Accessibility Features', icon: <Accessibility /> },
    { value: 'mobile_responsive', label: 'Mobile Responsiveness', icon: <PhoneAndroid /> },
    { value: 'bug_fixes', label: 'Bug Fixes & Stability', icon: <BugReport /> },
    { value: 'new_features', label: 'New Feature Request', icon: <Lightbulb /> },
  ];

  // Common UI/UX issues
  const commonIssues = [
    'Slow loading times',
    'Confusing navigation',
    'Poor mobile experience',
    'Inconsistent design elements',
    'Lack of visual feedback',
    'Complex user flows',
    'Accessibility barriers',
    'Outdated visual design',
    'Limited search/filter options',
    'Poor error handling',
  ];

  // Proposed solutions
  const commonSolutions = [
    'Implement advanced filtering and search',
    'Add real-time status updates',
    'Improve visual hierarchy and typography',
    'Add progress indicators and loading states',
    'Implement responsive design patterns',
    'Add keyboard navigation support',
    'Create consistent color scheme and branding',
    'Add data visualization charts',
    'Implement drag-and-drop functionality',
    'Add bulk action capabilities',
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleMultiSelect = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim() && !formData.customDescription.trim()) {
      newErrors.description = 'Please provide a description or select issues/solutions';
    }

    if (!formData.enhancementType) {
      newErrors.enhancementType = 'Please select an enhancement type';
    }

    if (!captchaToken) {
      newErrors.captcha = 'Please complete the captcha';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateDescription = () => {
    let description = "UI/UX Enhancement Request\n\n";
    
    if (formData.enhancementType) {
      const selectedType = enhancementTypes.find(type => type.value === formData.enhancementType);
      description += `Enhancement Type: ${selectedType?.label}\n\n`;
    }

    if (formData.currentIssues.length > 0) {
      description += "Current Issues Identified:\n";
      formData.currentIssues.forEach(issue => {
        description += `• ${issue}\n`;
      });
      description += "\n";
    }

    if (formData.proposedSolutions.length > 0) {
      description += "Proposed Solutions:\n";
      formData.proposedSolutions.forEach(solution => {
        description += `• ${solution}\n`;
      });
      description += "\n";
    }

    description += `User Impact Level: ${formData.userImpact}/5\n`;
    description += `Implementation Complexity: ${formData.implementationComplexity}/5\n`;
    description += `Priority: ${formData.priority}\n`;

    if (formData.customDescription.trim()) {
      description += `\nAdditional Details:\n${formData.customDescription}`;
    }

    return description;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const ticketData = {
        case: {
          new_name: formData.title,
          new_description: generateDescription(),
          new_cat1srng: formData.category1,
          new_cat2srng: formData.category2,
          new_cat3srng: formData.category3,
          new_prioritycode: formData.priority === 'High' ? 1 : formData.priority === 'Medium' ? 2 : 3,
          token: captchaToken,
        }
      };

      await onSubmit(ticketData);
      
      // Reset form
      setFormData({
        title: 'Enhance User Interface and Experience - Case History',
        description: '',
        priority: 'Medium',
        category1: 'IT Support',
        category2: 'Software Enhancement',
        category3: 'User Interface',
        enhancementType: '',
        currentIssues: [],
        proposedSolutions: [],
        userImpact: 3,
        implementationComplexity: 3,
        urgency: 'Medium',
        customDescription: '',
      });
      setCaptchaToken('');
      onClose();
    } catch (error) {
      console.error('Error submitting ticket:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Lightbulb />
        Create UI/UX Enhancement Ticket
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info">
            <AlertTitle>UI/UX Enhancement Request</AlertTitle>
            This form helps you create a detailed ticket for user interface and experience improvements.
          </Alert>
        </Box>

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Ticket Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              variant="outlined"
            />
          </Grid>

          {/* Enhancement Type */}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.enhancementType}>
              <InputLabel>Enhancement Type</InputLabel>
              <Select
                value={formData.enhancementType}
                label="Enhancement Type"
                onChange={(e) => handleInputChange('enhancementType', e.target.value)}
              >
                {enhancementTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority and Categories */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleInputChange('priority', e.target.value)}
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Category 1"
              value={formData.category1}
              onChange={(e) => handleInputChange('category1', e.target.value)}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Category 2"
              value={formData.category2}
              onChange={(e) => handleInputChange('category2', e.target.value)}
              variant="outlined"
            />
          </Grid>

          {/* Impact Assessments */}
          <Grid item xs={12} sm={6}>
            <Typography gutterBottom>User Impact Level</Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={formData.userImpact}
                onChange={(e, value) => handleInputChange('userImpact', value)}
                min={1}
                max={5}
                marks
                step={1}
                valueLabelDisplay="on"
                color="primary"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              1 = Low Impact, 5 = High Impact
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography gutterBottom>Implementation Complexity</Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={formData.implementationComplexity}
                onChange={(e, value) => handleInputChange('implementationComplexity', value)}
                min={1}
                max={5}
                marks
                step={1}
                valueLabelDisplay="on"
                color="secondary"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              1 = Simple, 5 = Very Complex
            </Typography>
          </Grid>

          {/* Current Issues */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Current Issues (Optional)</Typography>
                {formData.currentIssues.length > 0 && (
                  <Chip 
                    label={`${formData.currentIssues.length} selected`} 
                    size="small" 
                    sx={{ ml: 2 }} 
                  />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {commonIssues.map((issue) => (
                    <FormControlLabel
                      key={issue}
                      control={
                        <Checkbox
                          checked={formData.currentIssues.includes(issue)}
                          onChange={(e) => handleMultiSelect('currentIssues', issue, e.target.checked)}
                        />
                      }
                      label={issue}
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Proposed Solutions */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Proposed Solutions (Optional)</Typography>
                {formData.proposedSolutions.length > 0 && (
                  <Chip 
                    label={`${formData.proposedSolutions.length} selected`} 
                    size="small" 
                    sx={{ ml: 2 }} 
                  />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {commonSolutions.map((solution) => (
                    <FormControlLabel
                      key={solution}
                      control={
                        <Checkbox
                          checked={formData.proposedSolutions.includes(solution)}
                          onChange={(e) => handleMultiSelect('proposedSolutions', solution, e.target.checked)}
                        />
                      }
                      label={solution}
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Custom Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Details (Optional)"
              value={formData.customDescription}
              onChange={(e) => handleInputChange('customDescription', e.target.value)}
              placeholder="Provide any additional details, specific requirements, or context..."
              variant="outlined"
            />
          </Grid>

          {/* Preview */}
          {(formData.enhancementType || formData.currentIssues.length > 0 || formData.proposedSolutions.length > 0) && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>Description Preview:</Typography>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {generateDescription()}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Captcha */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <HCaptcha
                sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken('')}
              />
            </Box>
            {errors.captcha && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {errors.captcha}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? null : <CheckCircle />}
        >
          {isSubmitting ? 'Creating Ticket...' : 'Create Enhancement Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UIEnhancementTicket;
